import { Injectable } from '@angular/core';
import * as moment from 'moment-timezone';

/** Un campo que MOBICO ajustó: valor de antes y de ahora (crudos, como los guarda el API). */
export interface MobicoAdjustmentChange {
  field: 'product' | 'delivery_date' | 'liters' | 'po_number' | 'material' | 'material_code' | string;
  from: any;
  to: any;
}

/** Ajuste de MOBICO sobre un pedido (FuelOrder.mobico_adjustments, lo sirve /purchases como `adjustments`). */
export interface MobicoAdjustment {
  at: string | Date;
  reason?: string | null;
  /** approval = al responder la solicitud · edit = cambio directo del admin sobre un pedido ya aprobado. */
  kind?: 'approval' | 'edit';
  /** Solicitud de cambio que se aprobó con el ajuste (null si se aprobó el pedido tal cual). */
  change_id?: string | null;
  changes: MobicoAdjustmentChange[];
}

/** Línea legible de un ajuste («Producto: Extra → Supreme»). */
export interface AdjustmentLine {
  field: string;
  label: string;
  from: string;
  to: string;
}

/** Nombre comercial MOBICO de un producto interno (regular → Extra…): lo pone cada componente con su catálogo. */
export type ProductLabeler = (product: string) => string;

/** Fila del historial de un pedido que nace de un ajuste de MOBICO (una por campo ajustado). */
export interface AdjustmentHistoryRow {
  _id: string;
  order_id: string;
  change_kind: 'product' | 'date' | 'capacity';
  type_label: string;
  request_date: string | Date;
  po_number: string | null;
  product: string;
  material: string | null;
  truck_capacity: number | null;
  order_delivery_date: string | null;
  new_value: string;
  po_new: string | null;
  history_reason: string;
  status: 'confirmed';
  statusName: string;
  status_label: string;
  /** Nace de un ajuste de MOBICO, no de una solicitud de cambio (la fila del pedido no lo toma como «último estatus»). */
  is_adjustment: true;
  /** Columnas que ESTE ajuste cambió (po_number, product, material, truck_capacity, order_delivery_date): el resto se
   *  completa con el estado del pedido en ese momento, ver fillAdjustmentContext. */
  _adjusted_fields: string[];
  _ts: number;
  _event_ts: number;
  [extra: string]: any;
}

/** Etiquetas de los campos que se muestran de un ajuste (el material no se enseña). */
const ADJUSTMENT_LABELS: { [field: string]: string } = {
  product: 'Producto',
  delivery_date: 'Fecha de entrega',
  liters: 'Litros',
  po_number: 'PO',
};

/** Tipo de solicitud del Formato de cambios con el que se presenta cada campo ajustado. */
const CHANGE_KIND_BY_FIELD: { [field: string]: 'product' | 'date' | 'capacity' } = {
  product: 'product',
  delivery_date: 'date',
  liters: 'capacity',
};

const ADJUSTED_STATUS_LABEL = 'Ajustado por MOBICO';

/** Columnas de una fila del historial que describen la solicitud tal como estaba antes del evento. */
const CONTEXT_FIELDS = ['po_number', 'product', 'material', 'truck_capacity', 'order_delivery_date'] as const;

/**
 * Ajustes de MOBICO al aprobar (usuario, 2026-10-16): el admin aceptó otro producto, otra fecha de entrega u otros
 * litros que los solicitados. Cada ajuste trae el valor de antes, el de ahora y el motivo; si cambió el producto, el PO
 * también cambió (el anterior ya no se reutiliza). Aquí vive todo lo que las dos tablas (solicitante y panel admin)
 * muestran de un ajuste: tooltips, filas del historial y resumen del aviso. Métodos puros (sin estado ni HTTP).
 */
@Injectable({ providedIn: 'root' })
export class PipesAdjustmentsService {
  private readonly numberFormat = new Intl.NumberFormat('es-MX');

  /** Texto de cada campo de un ajuste (valor de antes → de ahora): producto, fecha, litros y PO; el material no se muestra. */
  adjustmentLines(adjustment: MobicoAdjustment | null | undefined, productLabel: ProductLabeler): AdjustmentLine[] {
    return (adjustment?.changes || [])
      .filter((c) => ADJUSTMENT_LABELS[c.field])
      .map((c) => ({
        field: c.field,
        label: ADJUSTMENT_LABELS[c.field],
        from: this.show(c.field, c.from, productLabel),
        to: this.show(c.field, c.to, productLabel),
      }));
  }

  /** Tooltip de la marca «aprobada con ajustes» del pedido: cada ajuste con su valor anterior y el motivo (más reciente primero). */
  adjustmentsTitle(adjustments: MobicoAdjustment[] | null | undefined, productLabel: ProductLabeler): string {
    return [...(adjustments || [])]
      .sort((a, b) => new Date(b.at).getTime() - new Date(a.at).getTime())
      .map((a) =>
        [
          ...this.adjustmentLines(a, productLabel).map((l) => `${l.label}: ${l.from} → ${l.to}`),
          a.reason ? `Motivo: ${a.reason}` : '',
        ]
          .filter(Boolean)
          .join('\n'),
      )
      .join('\n\n');
  }

  /** «producto Extra → Supreme · litros 30,000 → 3,000»: lo que cambió un ajuste (sin el PO ni el material). */
  adjustmentSummary(adjustment: MobicoAdjustment, productLabel: ProductLabeler): string {
    return this.adjustmentLines(adjustment, productLabel)
      .filter((l) => l.field !== 'po_number')
      .map((l) => `${l.label.toLowerCase()} ${l.from} → ${l.to}`)
      .join(' · ');
  }

  /** Tooltip de la celda «PO» de una fila del historial: el PO al que pasó el pedido (este ya no se reutiliza). */
  historyPoTitle(row: { po_new?: string | null } | null | undefined): string {
    return row?.po_new ? `El pedido pasó al PO ${row.po_new}. Este PO ya no se reutiliza.` : '';
  }

  /**
   * Filas del historial de un pedido por sus ajustes de MOBICO: una por campo ajustado, con lo pedido ANTES del ajuste
   * en las columnas (PO, producto, material, capacidad, fecha), lo que MOBICO aceptó en «Nuevo valor» y el motivo en el
   * tooltip. `base` son los campos que cada tabla hereda a todas sus filas de historial (is_history, estación…).
   */
  historyRows(
    order: { _id: any; po_number?: string | null; product?: string; material?: string | null; truck_capacity?: number | null; order_delivery_date?: string | null; adjustments?: MobicoAdjustment[] | null },
    base: { [key: string]: any },
    changeTypeLabels: { [kind: string]: string },
    productLabel: ProductLabeler,
  ): AdjustmentHistoryRow[] {
    const rows: AdjustmentHistoryRow[] = [];
    const orderId = String(order._id);
    (order.adjustments || []).forEach((a, i) => {
      const change = (field: string) => (a.changes || []).find((c) => c.field === field);
      const before = (field: string) => change(field)?.from;
      const productBefore = before('product');
      const ts = new Date(a.at).getTime();
      const adjustedFields = [
        change('po_number') ? 'po_number' : null,
        productBefore != null ? 'product' : null,
        productBefore != null || change('material') ? 'material' : null,
        change('liters') ? 'truck_capacity' : null,
        change('delivery_date') ? 'order_delivery_date' : null,
      ].filter(Boolean) as string[];
      this.adjustmentLines(a, productLabel)
        .filter((l) => l.field !== 'po_number')
        .forEach((l) => {
          const kind = CHANGE_KIND_BY_FIELD[l.field];
          rows.push({
            ...base,
            _id: `adj-${orderId}-${i}-${l.field}`,
            order_id: orderId,
            change_kind: kind,
            type_label: changeTypeLabels[kind],
            request_date: a.at,
            po_number: before('po_number') ?? order.po_number ?? null,
            product: productBefore ?? order.product,
            // Sin material guardado, el nombre comercial del producto que se dejó.
            material: before('material') ?? (productBefore ? productLabel(productBefore) : order.material ?? null),
            truck_capacity: before('liters') ?? order.truck_capacity ?? null,
            // Las fechas del ajuste son días (YYYY-MM-DD); la tabla las pinta como medianoche UTC igual que el API.
            order_delivery_date: before('delivery_date') ? `${before('delivery_date')}T00:00:00.000Z` : order.order_delivery_date ?? null,
            new_value: l.to,
            // PO al que pasó el pedido con el cambio de producto (el anterior ya no se reutiliza).
            po_new: l.field === 'product' ? change('po_number')?.to ?? null : null,
            history_reason: a.reason ? `Motivo de MOBICO: ${a.reason}` : '',
            status: 'confirmed',
            statusName: ADJUSTED_STATUS_LABEL,
            status_label: ADJUSTED_STATUS_LABEL,
            is_adjustment: true,
            _adjusted_fields: adjustedFields,
            _ts: ts,
            _event_ts: ts,
          });
        });
    });
    return rows;
  }

  /**
   * Completa en las filas de ajuste lo que el ajuste NO tocó con el estado del pedido en ESE momento. El API solo
   * guarda los campos ajustados, y historyRows cae a lo vigente del pedido, que pudo cambiar después: un ajuste de
   * litros hecho cuando el pedido era Supreme no debe pintarse Extra porque luego regresó a Extra. Lo de ANTES de cada
   * evento es lo de DESPUÉS del inmediato anterior, así que basta recorrer el historial de más reciente a más antiguo;
   * solo el evento más reciente hereda lo vigente. `rows` va ordenado más reciente arriba y se modifica en sitio.
   */
  fillAdjustmentContext<T extends { is_adjustment?: boolean; _adjusted_fields?: string[]; [key: string]: any }>(rows: T[]): T[] {
    let after: { [field: string]: any } | null = null;
    rows.forEach((row) => {
      const prev = after;
      if (row.is_adjustment && prev) {
        const touched = row._adjusted_fields || [];
        CONTEXT_FIELDS.forEach((field) => {
          if (!touched.includes(field)) { (row as any)[field] = prev[field]; }
        });
      }
      const before: { [field: string]: any } = {};
      CONTEXT_FIELDS.forEach((field) => { before[field] = row[field] ?? null; });
      after = before;
    });
    return rows;
  }

  /** Valor de un campo tal como lo lee el usuario: producto comercial, día DD/MM/YY, litros con separador de miles. */
  private show(field: string, value: any, productLabel: ProductLabeler): string {
    if (value == null || value === '') { return '—'; }
    if (field === 'product') { return productLabel(value); }
    if (field === 'delivery_date') { return moment.utc(value).format('DD/MM/YY'); }
    if (field === 'liters') { return this.numberFormat.format(Number(value)); }
    return String(value);
  }
}
